import type { PartDefinition } from "@rogue/rackbuilder"
import { getRackModel } from "../../modelPaths"
import type { MassStorageOptions } from "../../RackTypes"
import { getMsHardwareCount, type MsUnitShelfData } from "../../utils/ms-utils/ms-hardware-counting-utils"
import { assignShelfPlacement } from "../../utils/ms-utils/ms-shelf-utils"
import { addVirtualMeshesToUnt } from "../../utils/mms-utils/mms-virtual-mesh-utils"
import { MS_VIRTUAL_MESH_CONFIG } from "../../utils/ms-utils/ms-virtual-mesh-config"
import { addMassStorageUnitSide } from "../../utils/ms-utils/ms-addon-utils"
import { getMsBoltPackCount } from "../../utils/ms-utils/ms-bolt-utils"
import type { BuyRequest } from "@/js/services/RogueTypes"

export class MassStorageBuilder {
  constructor(private config: MassStorageOptions, private buyRequest: BuyRequest) {}

  private buildSide(left: boolean, frontWheel: PartDefinition, backWheel: PartDefinition): PartDefinition {
    const frontUpright: PartDefinition = {
      mesh  : getRackModel('PARTS', `upright ${this.config.height}`),
      slots : {},
    }

    const backUpright: PartDefinition = {
      mesh        : getRackModel('PARTS', `upright ${this.config.height}`),
      slots       : {},
      adjustments : [{ type: 'rotate', axis: 'Y', amount: 180 }],
    }

    const topCrossmember: PartDefinition = {
      mesh        : getRackModel('PARTS', 'crossmember'),
      slots       : {},
      anchor      : left ? '' : 'right',
      adjustments : left ? [] : [{ type: 'rotate', axis: 'Y', amount: 180 }],
    }

    return {
      mesh  : getRackModel('PARTS', 'base'),
      slots : {
        front                                      : frontUpright,
        back                                       : backUpright,
        [`crossmember_top_${this.config.height}h`] : topCrossmember,
        front_wheel                                : this.config.footSelection === 'wheels' ? frontWheel : [],
        back_wheel                                 : this.config.footSelection === 'wheels' ? backWheel : [],
      },
      anchor: 'base',
    }
  }

  public build(): PartDefinition {
    const frontWheel: PartDefinition = {
      mesh  : getRackModel('PARTS', 'wheel'),
      slots : {},
    }

    const backWheel: PartDefinition = {
      mesh        : getRackModel('PARTS', 'wheel'),
      slots       : {},
      adjustments : [{ type: 'rotate', axis: 'Y', amount: 180 }],
    }

    const leftBase  = this.buildSide(true, frontWheel, backWheel)
    const rightBase = this.buildSide(false, frontWheel, backWheel)

    // Must match MonsterMassStorageBuilder's attachment order — it drives shared unit-labeling/accordion-index logic in mms-virtual-mesh-utils.ts.
    leftBase.slots[`right_side_${this.config.width}h`] = rightBase

    // Base Shelf Selections
    const baseShelfData = assignShelfPlacement(leftBase, {
      shelfOneSelection        : this.config.shelfOneSelection,
      shelfTwoSelection        : this.config.shelfTwoSelection,
      shelfThreeSelection      : this.config.shelfThreeSelection,
      shelfFourSelection       : this.config.shelfFourSelection,
      shelfFiveSelection       : this.config.shelfFiveSelection,
      shelfOneHangerQuantity   : this.config.shelfOneHangerQuantity,
      shelfTwoHangerQuantity   : this.config.shelfTwoHangerQuantity,
      shelfThreeHangerQuantity : this.config.shelfThreeHangerQuantity,
      shelfFourHangerQuantity  : this.config.shelfFourHangerQuantity,
      shelfFiveHangerQuantity  : this.config.shelfFiveHangerQuantity,
      heightSelection          : this.config.height,
    }, this.config)

    // Create right and left addon shelves
    if (this.config.leftAddonChain.length || this.config.rightAddonChain.length) {
      addMassStorageUnitSide(leftBase, this.config.leftAddonChain, 'left', this.config, frontWheel, backWheel, this.buyRequest)
      addMassStorageUnitSide(rightBase, this.config.rightAddonChain, 'right', this.config, frontWheel, backWheel, this.buyRequest)
    } else {
      // Single unit: count the base at the exact positions it renders at (baseShelfData
      // from assignShelfPlacement) — the same source the addon path uses — so the bolt
      // count stays in lock-step with the geometry.
      const baseUnitShelfData: MsUnitShelfData[] = [{
        unit             : 0,
        unitHeight       : this.config.height,
        shelves          : baseShelfData,
        isEndUnit        : true, // Only one unit means it's both start and end
        adjacentToCorner : false, // Only one unit means no corners
      }]

      const baseHardwareCount = getMsHardwareCount(baseUnitShelfData)

      // MS stores its bolt count as 4-pack / 16-pack quantities rather than shared/solo.
      if (this.buyRequest.children?.[0]?.metadata) {
        this.buyRequest.children[0].metadata.hardwareCount = getMsBoltPackCount(baseHardwareCount)
      }
    }

    const virtualMeshCounter = { value: 0 }
    addVirtualMeshesToUnt(leftBase, undefined, this.config, virtualMeshCounter, MS_VIRTUAL_MESH_CONFIG)

    return leftBase
  }
}
